/**
 * OnboardingBoard — the 2-pane HR onboarding cockpit.
 *
 * Pattern (matches Linear / Front / Notion / Pipedrive):
 *   - LEFT pane: filterable, rich-row candidate list (avatar, track, start
 *     date, progress bar, urgency indicators).
 *   - RIGHT pane: sticky candidate header + horizontal section tabs +
 *     section content (Profile / Offer & LOA / Identity / Work Pass /
 *     Banking / IT / Day 1 / Hire).
 *
 * URL contract (extends EmployeeLifecycle's tab persistence):
 *   ?ref=<public_ref>     → which candidate is selected
 *   ?section=<section_id> → which section tab is active in the right pane
 *
 * The board is also reachable via the legacy path-style deep-link
 * /admin/employee-lifecycle/onboarding/:publicRef (preserved for backward
 * compat); EmployeeLifecycle.js promotes the path param into ?ref= on mount.
 *
 * Replaces the previous flow where intake (NewPreOnboarding.js) was a
 * separate full-page form. Now intake is a slim 4-field modal opened from
 * "+ New" at the top of the left pane; on save the user is dropped on the
 * board with the freshly-created candidate selected, exactly as in Linear
 * when you cmd-N a new issue.
 *
 * Data layer is the existing preOnboardingApi.js; no backend changes.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from '../../utils/toast';
import {
  AlertCircle,
  ArrowRight,
  BadgeCheck,
  Banknote,
  CalendarCheck,
  CheckCircle2,
  Circle,
  Clock,
  Copy,
  ExternalLink,
  FileSignature,
  Inbox,
  Laptop,
  Plus,
  Search,
  ShieldAlert,
  Sparkles,
  Upload,
  User,
  UserCheck,
  X,
} from 'lucide-react';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import FormSelect from '../../components/UI/FormSelect';
import { ClassificationBadge } from '../../components/UI/ClassificationBadge';
import WorkPassApplicationForm from '../../components/preonboarding/workpass/WorkPassApplicationForm';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import { StatusBadge } from '../../components/UI/StatusBadge';
import Portal from '../../components/UI/Portal';
import PillToggle from '../../components/UI/PillToggle';
import PillSelect from '../../components/UI/PillSelect';
import {
  QUEUE_ACTIVE,
  QUEUE_HIRED,
  QUEUE_OPTIONS,
  QUEUE_PANE,
  TRIAGE_ANY,
  TRIAGE_OPTIONS,
  filterOnboardingApplications,
  formatCandidateListMeta,
  isHiredStatus,
  queueCounts,
  queueForApplication,
  reconcileSelection,
  staffIdOf,
  startUrgency,
} from '../../utils/onboardingBoardFilters';
import preOnboardingAPI, {
  CLASSIFICATION_LABELS,
  CLASSIFICATION_WORKER,
  DOC_EDUCATION_CERT,
  DOC_NRIC,
  DOC_OFFER_LETTER,
  DOC_PASSPORT,
  DOC_SIGNED_OFFER,
  DOC_WORK_PASS_BACK,
  DOC_WORK_PASS_FRONT,
  DOCUMENT_TYPE_LABELS,
  PASS_EP,
  PASS_LABELS,
  PASS_NA,
  PASS_SP,
  PASS_WP,
  SELECTABLE_CLASSIFICATIONS,
  SOURCE_HR_MANUAL,
  SOURCE_LABELS,
  STATUS_HIRED,
  STATUS_READY_TO_HIRE,
  TRACK_LABELS,
  TRACK_LOCAL_PR,
  TRACK_PENDING,
  TRACK_S_PASS_EP,
  TRACK_WORK_PERMIT,
} from '../../services/preOnboardingApi';

// ============================================================================
// CONSTANTS
// ============================================================================

const SECTIONS = [
  { key: 'profile', label: 'Profile', icon: User },
  { key: 'offer', label: 'Offer & LOA', icon: FileSignature },
  { key: 'identity', label: 'Identity', icon: BadgeCheck },
  { key: 'workpass', label: 'Work Pass & MOM', icon: ShieldAlert, requiresWP: true },
  { key: 'banking', label: 'Banking', icon: Banknote },
  { key: 'it', label: 'IT Setup', icon: Laptop },
  { key: 'day1', label: 'Day 1', icon: CalendarCheck },
  { key: 'hire', label: 'Hire', icon: UserCheck },
];

const employeeRecordHref = (staffId) =>
  `/admin/employees?employee=${encodeURIComponent(staffId)}`;

// ============================================================================
// HELPERS
// ============================================================================

const initials = (name) => {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || '?';
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - now) / (1000 * 60 * 60 * 24));
  return diff;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-SG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const TRACK_AVATAR_COLOR = {
  [TRACK_WORK_PERMIT]: 'bg-amber-100 text-amber-800',
  [TRACK_S_PASS_EP]: 'bg-purple-100 text-purple-800',
  [TRACK_LOCAL_PR]: 'bg-emerald-100 text-emerald-800',
};


// Crude per-section completion heuristic. Until the workflow registry exposes
// a section-grouping field, we map registry step keys to our 8 sections by
// substring match. This is good-enough for the UI dot indicators; the source
// of truth for individual step state still comes from workflow.steps.
const SECTION_STEP_PATTERNS = {
  profile: ['profile', 'application_form', 'intake'],
  offer: ['offer', 'loa', 'appointment_letter', 'affirmation'],
  identity: ['passport', 'nric', 'fin', 'education_cert', 'identity'],
  workpass: ['wp_', 'work_pass', 'ipa', 'mcf', 'mom', 'medical', 'face_recognition', 'pcp', 'onboard_centre'],
  banking: ['bank', 'payroll'],
  it: ['dto_', 'mis_', 'laptop', 'google', 'email', 'work_email', 'gmail'],
  day1: ['day_1', 'day1', 'orientation', 'mentor', 'safety_sic', 'reporting'],
  hire: ['hire', 'materialise', 'ready_to_hire'],
};

const computeSectionStates = (workflow) => {
  if (!workflow || !Array.isArray(workflow.steps)) return {};
  const states = {};
  Object.entries(SECTION_STEP_PATTERNS).forEach(([sectionKey, patterns]) => {
    const matching = workflow.steps.filter((s) =>
      patterns.some((p) => (s.key || '').toLowerCase().includes(p))
    );
    if (matching.length === 0) {
      states[sectionKey] = 'empty';
      return;
    }
    const done = matching.filter((s) => s.status === 'done').length;
    const blocked = matching.some((s) => s.status === 'blocked');
    if (blocked) states[sectionKey] = 'blocked';
    else if (done === matching.length) states[sectionKey] = 'done';
    else if (done > 0) states[sectionKey] = 'in_progress';
    else states[sectionKey] = 'pending';
  });
  return states;
};

const SECTION_STATE_DOT = {
  done: 'bg-green-500',
  in_progress: 'bg-blue-500',
  blocked: 'bg-amber-500',
  pending: 'bg-gray-300',
  empty: 'bg-gray-200',
};


const URGENCY_TONE = {
  red: 'bg-red-100 text-red-700',
  amber: 'bg-amber-100 text-amber-700',
  green: 'bg-green-100 text-green-700',
  blue: 'bg-blue-100 text-blue-700',
  gray: 'bg-gray-100 text-gray-700',
};

const computeProgress = (workflow) => {
  if (!workflow || !Array.isArray(workflow.steps) || workflow.steps.length === 0) {
    return { done: 0, total: 0, pct: 0 };
  }
  const total = workflow.steps.length;
  const done = workflow.steps.filter((s) => s.status === 'done').length;
  return { done, total, pct: Math.round((done / total) * 100) };
};

// Find a step by registry key across the workflow.steps array. Many of the
// section actions (mark IT laptop notified, etc.) are simple completeStep
// calls keyed off these registry strings.
const findStep = (workflow, key) => {
  if (!workflow?.steps) return null;
  return workflow.steps.find((s) => s.key === key) || null;
};

// d2: a candidate is "awaiting HR review" until HR has consciously resolved
// BOTH the classification and a concrete (non-pending) track. While in this
// state the backend holds the workflow / welcome email, so there are no steps
// to action yet — we surface a banner explaining why.
const isAwaitingHrReview = (application) => {
  if (!application) return false;
  const classificationSet = Boolean(application.applicant_classification);
  const trackResolved =
    application.track && application.track !== TRACK_PENDING;
  return !(classificationSet && trackResolved);
};

// ============================================================================
// CANDIDATE LIST (LEFT PANE)
// ============================================================================

const CandidateListItem = ({ application, isSelected, onSelect }) => {
  const name = application.full_name || application.preferred_name || '(no name)';
  const trackLabel = TRACK_LABELS[application.track] || application.track || '';
  const trackColor = TRACK_AVATAR_COLOR[application.track] || 'bg-gray-100 text-gray-700';
  const hired = isHiredStatus(application.status);
  const meta = formatCandidateListMeta(application, {
    trackLabel,
    startDate: formatDate(application.offered_start_date),
  });
  const urgency = startUrgency(application);
  const progressPct = hired ? null : (application.workflow_progress?.pct ?? null);

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={isSelected ? 'true' : undefined}
      className={`w-full text-left p-4 hover:bg-red-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-inset ${
        isSelected ? 'bg-red-50' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className={`w-10 h-10 rounded-full ${trackColor} flex items-center justify-center text-sm font-semibold flex-shrink-0`}
            title={trackLabel || undefined}
            aria-hidden="true"
          >
            {initials(name)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
            {(meta.lead || meta.trail) && (
              <p className="text-xs mt-0.5 truncate">
                {meta.lead && (
                  <span className="font-medium text-gray-800">{meta.lead}</span>
                )}
                {meta.lead && meta.trail && (
                  <span className="text-gray-400"> · </span>
                )}
                {meta.trail && (
                  <span className="text-gray-600">{meta.trail}</span>
                )}
              </p>
            )}
          </div>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={application.status} />
        </div>
      </div>
      {urgency && (
        <div className="mt-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${URGENCY_TONE[urgency.tone]}`}>
            {urgency.label}
          </span>
        </div>
      )}
      {progressPct !== null && (
        <div className="mt-2">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Progress</span>
            <span>{progressPct}%</span>
          </div>
          <div
            className="progress"
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="progress-bar-admin" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}
    </button>
  );
};

// ============================================================================
// "+ NEW ONBOARDING" MODAL
// ============================================================================

const NewOnboardingModal = ({ open, onClose, onCreated }) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    track: TRACK_LOCAL_PR,
    full_name: '',
    personal_email: '',
    offered_start_date: '',
  });
  const [errors, setErrors] = useState({});

  // Reset on open so the form is fresh every time.
  useEffect(() => {
    if (open) {
      setForm({
        track: TRACK_LOCAL_PR,
        full_name: '',
        personal_email: '',
        offered_start_date: '',
      });
      setErrors({});
    }
  }, [open]);

  if (!open) return null;

  const trackOptions = Object.entries(TRACK_LABELS).map(([value, label]) => ({ value, label }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const localErrors = {};
    if (!form.full_name.trim()) localErrors.full_name = 'Required';
    if (!form.personal_email.trim()) localErrors.personal_email = 'Required';
    if (!form.offered_start_date) localErrors.offered_start_date = 'Required';
    if (Object.keys(localErrors).length) {
      setErrors(localErrors);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        track: form.track,
        source: SOURCE_HR_MANUAL,
        full_name: form.full_name.trim(),
        personal_email: form.personal_email.trim(),
        offered_start_date: form.offered_start_date,
      };
      const resp = await preOnboardingAPI.createApplication(payload);
      toast.success(`Created ${resp.data.public_ref}`);
      onCreated(resp.data);
    } catch (err) {
      const data = err?.response?.data || {};
      if (typeof data === 'object' && !Array.isArray(data)) {
        setErrors((prev) => ({ ...prev, ...data }));
      }
      toast.error(data.detail || 'Could not create candidate.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Portal>
      <div className="modal-overlay">
        <div className="modal-container modal-container-sm">
          <div className="modal-header">
            <h3 className="modal-title flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-red-600" /> New Onboarding
            </h3>
            <button onClick={onClose} className="modal-close-admin" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-content space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Track *</label>
                <FormSelect
                  theme="admin"
                  options={trackOptions}
                  value={form.track}
                  onChange={(val) => setForm((f) => ({ ...f, track: val }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  className="input-admin"
                  value={form.full_name}
                  onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                  placeholder="As per passport / NRIC"
                  autoFocus
                />
                {errors.full_name && (
                  <p className="text-xs text-red-600 mt-1">{errors.full_name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Personal Email *</label>
                <input
                  type="email"
                  className="input-admin"
                  value={form.personal_email}
                  onChange={(e) => setForm((f) => ({ ...f, personal_email: e.target.value }))}
                  placeholder="candidate@example.com"
                />
                {errors.personal_email && (
                  <p className="text-xs text-red-600 mt-1">{errors.personal_email}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Offered Start Date *</label>
                <input
                  type="date"
                  className="input-admin"
                  value={form.offered_start_date}
                  onChange={(e) => setForm((f) => ({ ...f, offered_start_date: e.target.value }))}
                />
                {errors.offered_start_date && (
                  <p className="text-xs text-red-600 mt-1">{errors.offered_start_date}</p>
                )}
              </div>

              <p className="text-xs text-gray-500 bg-gray-50 rounded-xl p-3 border">
                You can finish entering candidate details (department, salary, banking, documents,
                etc.) inside the onboarding board after creating this draft.
              </p>
            </div>

            <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn btn-admin-outline">
                Cancel
              </button>
              <button type="submit" className="btn btn-success" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create & Open Board'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
};

// ============================================================================
// CANDIDATE HEADER (sticky top of right pane)
// ============================================================================

const CandidateHeader = ({ application, workflow, onCopyLink, onOpenPortalPreview }) => {
  const name = application.full_name || application.preferred_name || '(no name)';
  const trackLabel = TRACK_LABELS[application.track] || '—';
  const sourceLabel = SOURCE_LABELS[application.source] || application.source;
  const days = daysUntil(application.offered_start_date);
  const progress = computeProgress(workflow);
  const hired = isHiredStatus(application.status);
  const staffId = staffIdOf(application);

  return (
    <div className="border-b bg-white px-4 sm:px-6 py-4 flex-shrink-0">
      <div className="flex items-start justify-between gap-3 sm:gap-4 flex-wrap">
        <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
          <div
            className={`w-11 h-11 sm:w-14 sm:h-14 rounded-full ${
              TRACK_AVATAR_COLOR[application.track] || 'bg-gray-100 text-gray-700'
            } flex items-center justify-center text-base sm:text-lg font-bold flex-shrink-0`}
          >
            {initials(name)}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-2xl font-bold text-gray-900 truncate">{name}</h2>
            <div className="flex items-center gap-2 text-sm text-gray-600 mt-1 flex-wrap">
              <span>{trackLabel}</span>
              <span className="text-gray-300">·</span>
              <span>{application.public_ref}</span>
              <span className="text-gray-300">·</span>
              <span>via {sourceLabel}</span>
              <StatusBadge status={application.workflow_status || application.status} />
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-2 flex-wrap">
              <span className="flex items-center gap-1">
                <CalendarCheck className="w-3.5 h-3.5" />
                {hired ? 'Started' : 'Starts'} {formatDate(application.offered_start_date)}
              </span>
              {hired && staffId && (
                <a
                  href={employeeRecordHref(staffId)}
                  className="flex items-center gap-1 text-red-700 hover:text-red-800 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded"
                >
                  <UserCheck className="w-3.5 h-3.5" />
                  {staffId}
                </a>
              )}
              {!hired && days !== null && (
                <span
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${
                    days <= 3
                      ? URGENCY_TONE.red
                      : days <= 7
                      ? URGENCY_TONE.amber
                      : URGENCY_TONE.gray
                  }`}
                >
                  <Clock className="w-3 h-3" />
                  {days < 0 ? `${Math.abs(days)}d ago` : days === 0 ? 'today' : `${days}d to go`}
                </span>
              )}
              {application.department_name && (
                <span className="flex items-center gap-1">
                  <Inbox className="w-3.5 h-3.5" />
                  {application.department_name}
                </span>
              )}
            </div>
          </div>
        </div>

        {!hired && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={onCopyLink}
              className="btn btn-admin-outline btn-sm"
              title="Copy candidate magic-link URL"
            >
              <Copy className="w-4 h-4 mr-1" />
              Copy Link
            </button>
            <button
              type="button"
              onClick={onOpenPortalPreview}
              className="btn btn-admin-outline btn-sm"
              title="Open the candidate-facing portal in a new tab"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Portal Preview
            </button>
          </div>
        )}
        {hired && staffId && (
          <a
            href={employeeRecordHref(staffId)}
            className="btn btn-admin-primary btn-sm flex-shrink-0"
          >
            <ExternalLink className="w-4 h-4 mr-1" />
            Open employee
          </a>
        )}
      </div>

      {/* Progress bar */}
      {progress.total > 0 && (
        <div className="mt-3 flex items-center gap-3">
          <div className="flex-1 bg-gray-200 rounded-full h-2">
            <div
              className="bg-red-600 h-2 rounded-full transition-all"
              style={{ width: `${progress.pct}%` }}
            />
          </div>
          <span className="text-xs text-gray-600 font-medium whitespace-nowrap">
            {progress.done}/{progress.total} steps · {progress.pct}%
          </span>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SECTION TABS (horizontal, sticky under header)
// ============================================================================

// Section navigation inside the right pane. Per the design system this is a
// primary tab strip (the user's main navigation within the candidate detail
// view), so we use the standard pill `.tab-btn-admin` + `.active` toggle —
// NOT the `-sm` variant which is reserved for filter sub-toggles.
//
// Note: this strip lives ABOVE the right-pane scroll area (not inside it),
// so we don't need `position: sticky` — the parent layout pins it visually.
const SectionTabs = ({ sections, active, onChange, sectionStates }) => (
  <div className="border-b bg-white px-4 py-3 flex-shrink-0">
    <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
      {sections.map((s) => {
        const Icon = s.icon;
        const state = sectionStates?.[s.key];
        const dotColor = SECTION_STATE_DOT[state] || SECTION_STATE_DOT.empty;
        const isActive = active === s.key;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onChange(s.key)}
            className={`tab-btn-admin ${isActive ? 'active' : ''}`}
          >
            <Icon className="w-4 h-4" />
            {s.label}
            {state && state !== 'empty' && (
              <span
                className={`w-1.5 h-1.5 rounded-full ${dotColor} ${
                  isActive ? 'ring-2 ring-white/60' : ''
                }`}
              />
            )}
          </button>
        );
      })}
    </div>
  </div>
);

// ============================================================================
// SECTION COMPONENTS (one per tab)
// ============================================================================

// Inline-editable text field. Calls onSave only on blur if value changed,
// avoiding the noise of a per-keystroke PATCH while keeping the auto-save
// pattern users expect from modern apps (Linear / Notion).
const InlineField = ({ label, value, onSave, type = 'text', placeholder, options, hint }) => {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  const original = useRef(value ?? '');

  const commit = async () => {
    if (draft === original.current) return;
    try {
      await onSave(draft);
      original.current = draft;
    } catch {
      setDraft(original.current);
    }
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </label>
      {options ? (
        <FormSelect
          theme="admin"
          options={options}
          value={draft}
          onChange={(val) => {
            setDraft(val);
            // selects commit immediately
            onSave(val).then(() => {
              original.current = val;
            }).catch(() => setDraft(original.current));
          }}
        />
      ) : (
        <input
          type={type}
          className="input-admin"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
        />
      )}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
};

const SectionCard = ({ title, subtitle, children, action }) => (
  <section className="bg-white rounded-2xl border shadow-sm p-4 sm:p-6">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-4">
      <div className="min-w-0 flex-1">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{title}</h3>
        {subtitle && <p className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">{subtitle}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
    {children}
  </section>
);

// d2: HR-owned classification + work-authorisation routing. The ATS hands over
// identity + role only; HR resolves these two here, which is what unblocks the
// pre-onboarding workflow. Selecting "Worker" auto-locks the Work Permit route
// (a Worker is, by definition, a WP holder) — the backend derives + persists
// track=work_permit / pass_type=wp on save, so we render that route read-only.
const ClassificationTrackCard = ({ application, onPatch }) => {
  const classification = application.applicant_classification || '';
  const isWorker = classification === CLASSIFICATION_WORKER;
  const track = application.track || TRACK_PENDING;
  const awaiting = isAwaitingHrReview(application);

  const classificationOptions = [
    { value: '', label: '— Select classification —' },
    ...SELECTABLE_CLASSIFICATIONS.map((c) => ({
      value: c,
      label: CLASSIFICATION_LABELS[c] || c,
    })),
  ];

  const trackOptions = [
    { value: TRACK_PENDING, label: '— Select track —' },
    { value: TRACK_WORK_PERMIT, label: TRACK_LABELS[TRACK_WORK_PERMIT] },
    { value: TRACK_S_PASS_EP, label: TRACK_LABELS[TRACK_S_PASS_EP] },
    { value: TRACK_LOCAL_PR, label: TRACK_LABELS[TRACK_LOCAL_PR] },
  ];

  const passOptions = [
    { value: PASS_SP, label: PASS_LABELS[PASS_SP] },
    { value: PASS_EP, label: PASS_LABELS[PASS_EP] },
  ];

  const handleClassification = (val) => {
    // Worker -> backend derives track=WP / pass_type=wp; just send the role.
    onPatch({ applicant_classification: val });
  };

  const handleTrack = (val) => {
    const patch = { track: val };
    // Keep pass_type coherent with the chosen track so registry conditions
    // (PCP, onboard-centre, degree verification) evaluate correctly.
    if (val === TRACK_WORK_PERMIT) patch.pass_type = PASS_WP;
    else if (val === TRACK_LOCAL_PR) patch.pass_type = PASS_NA;
    else if (val === TRACK_S_PASS_EP) {
      patch.pass_type = [PASS_SP, PASS_EP].includes(application.pass_type)
        ? application.pass_type
        : PASS_SP;
    }
    onPatch(patch);
  };

  return (
    <SectionCard
      title="Classification & Work Authorisation"
      subtitle="HR-owned. Resolving both starts the pre-onboarding workflow."
      action={
        classification ? (
          <ClassificationBadge classification={classification} />
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20">
            <Clock className="w-3 h-3" />
            Unclassified
          </span>
        )
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Classification *
          </label>
          <FormSelect
            theme="admin"
            options={classificationOptions}
            value={classification}
            onChange={handleClassification}
          />
          <p className="text-xs text-gray-400 mt-1">
            Intern / Advisor playbooks are not enabled yet.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Work-Authorisation Track *
          </label>
          {isWorker ? (
            <>
              <div className="input-admin bg-gray-50 text-gray-700 flex items-center justify-between cursor-not-allowed">
                <span>{TRACK_LABELS[TRACK_WORK_PERMIT]}</span>
                <ShieldAlert className="w-4 h-4 text-gray-400" />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Auto-locked — a Worker is always a Work Permit holder.
              </p>
            </>
          ) : (
            <>
              <FormSelect
                theme="admin"
                options={trackOptions}
                value={track}
                onChange={handleTrack}
                disabled={!classification}
              />
              {!classification && (
                <p className="text-xs text-gray-400 mt-1">
                  Choose a classification first.
                </p>
              )}
            </>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Pass Type
          </label>
          {!isWorker && track === TRACK_S_PASS_EP ? (
            <FormSelect
              theme="admin"
              options={passOptions}
              value={[PASS_SP, PASS_EP].includes(application.pass_type) ? application.pass_type : PASS_SP}
              onChange={(val) => onPatch({ pass_type: val })}
            />
          ) : (
            <div className="input-admin bg-gray-50 text-gray-500 flex items-center cursor-not-allowed">
              {PASS_LABELS[application.pass_type] || '—'}
            </div>
          )}
          {!isWorker && track === TRACK_S_PASS_EP && (
            <p className="text-xs text-gray-400 mt-1">
              S Pass vs Employment Pass drives PCP &amp; degree-verification steps.
            </p>
          )}
        </div>
      </div>

      {awaiting && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            The onboarding workflow, welcome email and candidate magic-link are
            on hold until both a classification and a concrete track are set.
          </span>
        </div>
      )}
    </SectionCard>
  );
};

// Slim banner shown at the top of every section while the candidate is still
// awaiting HR classification/track, so HR always knows why no steps exist yet.
const HiredArchiveBanner = ({ application }) => {
  const staffId = staffIdOf(application);
  return (
    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-2xl flex items-start gap-3">
      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-green-900">Already hired</div>
        <p className="text-xs text-green-800 mt-0.5">
          {application.full_name} is an employee
          {staffId ? ` (${staffId})` : ''}. This record is kept here as the hire
          archive — Day-1 work now lives on the employee record.
        </p>
      </div>
      {staffId && (
        <a
          href={employeeRecordHref(staffId)}
          className="btn btn-admin-outline btn-sm flex-shrink-0"
        >
          Open employee
        </a>
      )}
    </div>
  );
};

const PendingReviewBanner = ({ onGoToProfile }) => (
  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3">
    <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold text-amber-900">
        Pending HR review
      </div>
      <p className="text-xs text-amber-800 mt-0.5">
        Set this candidate&apos;s classification and work-authorisation track to
        start the onboarding workflow and send the welcome email.
      </p>
    </div>
    {onGoToProfile && (
      <button
        type="button"
        onClick={onGoToProfile}
        className="btn btn-admin-outline btn-sm flex-shrink-0"
      >
        Set now
      </button>
    )}
  </div>
);

const ProfileSection = ({ application, onPatch }) => (
  <div className="space-y-4">
    <ClassificationTrackCard application={application} onPatch={onPatch} />
    <SectionCard title="Personal Details" subtitle="Identity and contact information">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InlineField
          label="Full Name (per passport)"
          value={application.full_name}
          onSave={(v) => onPatch({ full_name: v })}
        />
        <InlineField
          label="Preferred Name"
          value={application.preferred_name}
          onSave={(v) => onPatch({ preferred_name: v })}
        />
        <InlineField
          label="Gender"
          value={application.gender || ''}
          onSave={(v) => onPatch({ gender: v })}
          options={[
            { value: '', label: '— Select —' },
            { value: 'M', label: 'Male' },
            { value: 'F', label: 'Female' },
            { value: 'O', label: 'Other' },
          ]}
        />
        <InlineField
          label="Date of Birth"
          type="date"
          value={application.date_of_birth || ''}
          onSave={(v) => onPatch({ date_of_birth: v })}
        />
        <InlineField
          label="Nationality"
          value={application.nationality}
          onSave={(v) => onPatch({ nationality: v })}
        />
        <InlineField
          label="NRIC / FIN"
          value={application.nric_fin || ''}
          onSave={(v) => onPatch({ nric_fin: v })}
          hint="Sticky to the person across employers (per MOM rules)."
        />
      </div>
    </SectionCard>

    <SectionCard title="Contact" subtitle="How HR and the candidate communicate">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InlineField
          label="Personal Email"
          type="email"
          value={application.personal_email}
          onSave={(v) => onPatch({ personal_email: v })}
        />
        <InlineField
          label="Work Email (assigned by MIS)"
          type="email"
          value={application.work_email || ''}
          onSave={(v) => onPatch({ work_email: v })}
          hint="Set later when MIS provisions the Microsoft account."
        />
        <InlineField
          label="Mobile (Singapore)"
          value={application.mobile}
          onSave={(v) => onPatch({ mobile: v })}
          placeholder="+65 9xxx xxxx"
        />
      </div>
    </SectionCard>

    <SectionCard title="Source" subtitle="How this candidate entered the pipeline">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Source
          </label>
          <p className="text-sm text-gray-900">{SOURCE_LABELS[application.source] || application.source}</p>
        </div>
        {application.ats_candidate_id && (
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              ATS Candidate ID
            </label>
            <p className="text-sm text-gray-900 font-mono">{application.ats_candidate_id}</p>
          </div>
        )}
      </div>
    </SectionCard>
  </div>
);

const DocRow = ({ docType, docs, busy, onUpload }) => {
  const doc = docs.find((d) => d.document_type === docType);
  const inputRef = useRef(null);
  return (
    <div className="border rounded-xl p-3 flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900">
          {DOCUMENT_TYPE_LABELS[docType]}
        </div>
        {doc ? (
          <div className="text-xs text-gray-500 truncate">
            {doc.original_filename || 'file'} · uploaded {formatDateTime(doc.uploaded_at)}
            {doc.uploaded_by_staff_id ? ` by ${doc.uploaded_by_staff_id}` : ''}
          </div>
        ) : (
          <div className="text-xs text-gray-400 italic">Not yet uploaded</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {doc?.file_url && (
          <a
            href={doc.file_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-blue-600 hover:underline"
          >
            View
          </a>
        )}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="btn btn-admin-outline btn-sm"
        >
          <Upload className="w-3.5 h-3.5 mr-1" />
          {doc ? 'Replace' : 'Upload'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(docType, f);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
};

const OfferLOASection = ({ application, documents, onPatch, onUpload, busy }) => (
  <div className="space-y-4">
    <SectionCard title="Offer Details" subtitle="Salary, designation, start date">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InlineField
          label="Job Title"
          value={application.designation || ''}
          onSave={(v) => onPatch({ designation: v })}
        />
        <InlineField
          label="Department"
          value={application.department_name || ''}
          onSave={() => Promise.resolve()}
          hint="Edit via the department picker on the Profile section (coming next)."
        />
        <InlineField
          label="Offered Start Date"
          type="date"
          value={application.offered_start_date || ''}
          onSave={(v) => onPatch({ offered_start_date: v })}
        />
        <InlineField
          label="Basic Salary (SGD)"
          type="number"
          value={application.basic_salary || ''}
          onSave={(v) => onPatch({ basic_salary: v })}
        />
      </div>
    </SectionCard>

    <SectionCard
      title="Letter of Appointment (LOA)"
      subtitle="Generate, route to Mr Lim (S0001) for signature, then to candidate to counter-sign"
    >
      <div className="space-y-3">
        <DocRow docType={DOC_OFFER_LETTER} docs={documents} busy={busy === DOC_OFFER_LETTER} onUpload={onUpload} />
        <DocRow docType={DOC_SIGNED_OFFER} docs={documents} busy={busy === DOC_SIGNED_OFFER} onUpload={onUpload} />
      </div>
      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
        <strong>Coming next:</strong> in-portal LOA generation from a DOCX template, automated
        routing to S0001 for e-signature, then candidate counter-sign via the magic-link portal.
        For now, attach the manually-prepared LOA above.
      </div>
    </SectionCard>
  </div>
);

const IdentitySection = ({ documents, onUpload, busy }) => (
  <div className="space-y-4">
    <SectionCard title="Identity Documents" subtitle="Passport, NRIC/FIN, education certificates">
      <div className="space-y-3">
        <DocRow docType={DOC_PASSPORT} docs={documents} busy={busy === DOC_PASSPORT} onUpload={onUpload} />
        <DocRow docType={DOC_NRIC} docs={documents} busy={busy === DOC_NRIC} onUpload={onUpload} />
        <DocRow
          docType={DOC_EDUCATION_CERT}
          docs={documents}
          busy={busy === DOC_EDUCATION_CERT}
          onUpload={onUpload}
        />
      </div>
    </SectionCard>
  </div>
);

const WorkPassSection = ({
  application,
  documents,
  workflow,
  onSubmitWP,
  onCaptureIPA,
  onUpload,
  busy,
}) => {
  const [wpForm, setWpForm] = useState({
    passType: application.pass_type || PASS_EP,
    submittedAt: '',
  });
  const [ipaForm, setIpaForm] = useState({
    passType: application.pass_type || PASS_EP,
    ipaIssuedAt: '',
    file: null,
  });
  const [wpBusy, setWpBusy] = useState(false);
  const [ipaBusy, setIpaBusy] = useState(false);

  // Form B (Work Pass Application Form) — HR review + send-to-candidate.
  const [formB, setFormB] = useState(null);
  const [formBLoading, setFormBLoading] = useState(true);
  const [formBSending, setFormBSending] = useState(false);
  const [formBLink, setFormBLink] = useState('');

  useEffect(() => {
    let cancelled = false;
    setFormBLoading(true);
    preOnboardingAPI
      .getWorkPassForm(application.public_ref)
      .then((res) => {
        if (!cancelled) setFormB(res.data);
      })
      .catch(() => {
        if (!cancelled) setFormB(null);
      })
      .finally(() => {
        if (!cancelled) setFormBLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [application.public_ref]);

  const handleSendFormB = async () => {
    setFormBSending(true);
    try {
      const res = await preOnboardingAPI.sendWorkPassForm(application.public_ref);
      setFormB(res.data.form);
      setFormBLink(res.data.portal_url || '');
      toast.success(
        res.data.sent_to
          ? `Form B link sent to ${res.data.sent_to}`
          : 'Form B link generated',
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send Form B');
    } finally {
      setFormBSending(false);
    }
  };

  const formBStatus = formB?.status;
  const formBComplete = formBStatus === 'completed';

  const passOptions = Object.entries(PASS_LABELS).map(([v, l]) => ({ value: v, label: l }));
  const wpStep = findStep(workflow, 'wp_application_submitted');
  const ipaStep = findStep(workflow, 'ipa_received');

  const handleSubmitWP = async () => {
    setWpBusy(true);
    try {
      await onSubmitWP(wpForm);
    } finally {
      setWpBusy(false);
    }
  };

  const handleCaptureIPA = async () => {
    setIpaBusy(true);
    try {
      await onCaptureIPA(ipaForm);
      setIpaForm({ passType: ipaForm.passType, ipaIssuedAt: '', file: null });
    } finally {
      setIpaBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard
        title="Work Pass Application Form (Form B)"
        subtitle={
          formBComplete
            ? `Completed by candidate${formB?.completed_at ? ` ${formatDateTime(formB.completed_at)}` : ''}`
            : formBStatus === 'sent'
              ? `Sent to candidate${formB?.sent_at ? ` ${formatDateTime(formB.sent_at)}` : ''} — awaiting completion`
              : 'Prefilled from the application. Send a magic link for the candidate to complete the MOM work-pass fields.'
        }
      >
        {formBLink && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2">
            <input
              readOnly
              value={formBLink}
              className="input-admin flex-1 bg-white text-xs"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              className="btn btn-admin-secondary shrink-0"
              onClick={() => {
                navigator.clipboard?.writeText(formBLink);
                toast.success('Link copied');
              }}
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
        )}
        <WorkPassApplicationForm
          theme="admin"
          mode="hr-review"
          loading={formBLoading}
          values={formB?.values || {}}
          prefilledKeys={formB?.prefilled_keys || []}
          footer={
            <button
              type="button"
              className="btn btn-admin-primary"
              onClick={handleSendFormB}
              disabled={formBSending || formBLoading || formBComplete}
            >
              {formBSending
                ? 'Sending…'
                : formBComplete
                  ? 'Completed'
                  : formBStatus === 'sent'
                    ? 'Resend link'
                    : 'Send to candidate'}
            </button>
          }
        />
      </SectionCard>

      <SectionCard
        title="Submit Work Pass to MOM"
        subtitle={
          wpStep?.status === 'done'
            ? `Submitted ${formatDateTime(wpStep.completed_at)}`
            : 'Record the moment you lodge the WP/SP/EP application'
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Pass type</label>
            <FormSelect
              theme="admin"
              options={passOptions}
              value={wpForm.passType}
              onChange={(v) => setWpForm((f) => ({ ...f, passType: v }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Submitted at</label>
            <input
              type="datetime-local"
              className="input-admin"
              value={wpForm.submittedAt}
              onChange={(e) => setWpForm((f) => ({ ...f, submittedAt: e.target.value }))}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              className="btn btn-admin-primary w-full"
              onClick={handleSubmitWP}
              disabled={wpBusy}
            >
              {wpBusy ? 'Saving…' : 'Record Submission'}
            </button>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Capture IPA"
        subtitle={
          ipaStep?.status === 'done'
            ? `IPA captured ${formatDateTime(ipaStep.completed_at)}`
            : 'Upload the In-Principle Approval letter once MOM issues it'
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Pass type</label>
            <FormSelect
              theme="admin"
              options={passOptions}
              value={ipaForm.passType}
              onChange={(v) => setIpaForm((f) => ({ ...f, passType: v }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">IPA issued</label>
            <input
              type="date"
              className="input-admin"
              value={ipaForm.ipaIssuedAt}
              onChange={(e) => setIpaForm((f) => ({ ...f, ipaIssuedAt: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">IPA letter</label>
            <input
              type="file"
              accept="application/pdf,image/*"
              className="input-admin file:rounded-full file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
              onChange={(e) => setIpaForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              className="btn btn-admin-primary w-full"
              onClick={handleCaptureIPA}
              disabled={ipaBusy || !ipaForm.file}
            >
              {ipaBusy ? 'Saving…' : 'Capture IPA'}
            </button>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Work Pass Card"
        subtitle="Upload after candidate collects WP from MOM Service Centre"
      >
        <div className="space-y-3">
          <DocRow
            docType={DOC_WORK_PASS_FRONT}
            docs={documents}
            busy={busy === DOC_WORK_PASS_FRONT}
            onUpload={onUpload}
          />
          <DocRow
            docType={DOC_WORK_PASS_BACK}
            docs={documents}
            busy={busy === DOC_WORK_PASS_BACK}
            onUpload={onUpload}
          />
        </div>
      </SectionCard>
    </div>
  );
};

const BankingSection = ({ application, onPatch }) => (
  <div className="space-y-4">
    <SectionCard
      title="Banking Details"
      subtitle="Required for GIRO salary credit. Candidate can also self-fill via the magic-link portal."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InlineField
          label="Bank Name"
          value={application.bank_name || ''}
          onSave={(v) => onPatch({ bank_name: v })}
          placeholder="e.g. DBS, OCBC, UOB"
        />
        <InlineField
          label="Account Number"
          value={application.bank_account_number || ''}
          onSave={(v) => onPatch({ bank_account_number: v })}
        />
        <InlineField
          label="Account Holder Name"
          value={application.bank_account_holder || ''}
          onSave={(v) => onPatch({ bank_account_holder: v })}
          hint="Should match Full Name on the Profile section."
        />
        <InlineField
          label="Branch Code (optional)"
          value={application.bank_branch_code || ''}
          onSave={(v) => onPatch({ bank_branch_code: v })}
        />
      </div>
    </SectionCard>
  </div>
);

const StepRow = ({ stepKey, label, hint, workflow, onComplete, onSkip, actor }) => {
  const step = findStep(workflow, stepKey);
  const isDone = step?.status === 'done';
  const isBlocked = step?.status === 'blocked';
  const [busy, setBusy] = useState(false);

  return (
    <div
      className={`border rounded-xl p-3 flex items-center justify-between gap-3 ${
        isDone ? 'bg-green-50 border-green-200' : 'bg-white'
      }`}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {isDone ? (
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
        ) : isBlocked ? (
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        ) : (
          <Circle className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900">{label}</div>
          {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
          {actor && (
            <div className="text-[11px] text-gray-400 mt-0.5 uppercase tracking-wide">
              Owner: {actor}
            </div>
          )}
          {isDone && step?.completed_at && (
            <div className="text-xs text-green-700 mt-0.5">
              Completed {formatDateTime(step.completed_at)}
              {step.completed_by_staff_id ? ` by ${step.completed_by_staff_id}` : ''}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!isDone && step && (
          <>
            <button
              type="button"
              onClick={async () => {
                setBusy(true);
                try {
                  await onComplete(stepKey);
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="btn btn-success btn-sm"
            >
              {busy ? 'Saving…' : 'Mark Done'}
            </button>
            {onSkip && (
              <button
                type="button"
                onClick={async () => {
                  const reason = window.prompt('Reason for skipping?');
                  if (!reason) return;
                  setBusy(true);
                  try {
                    await onSkip(stepKey, reason);
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy}
                className="btn btn-admin-outline btn-sm"
              >
                Skip
              </button>
            )}
          </>
        )}
        {!step && (
          <span className="text-xs text-gray-400 italic">Not in registry yet</span>
        )}
      </div>
    </div>
  );
};

const ITSection = ({ workflow, onComplete, onSkip }) => (
  <div className="space-y-4">
    <SectionCard
      title="IT & Workspace Setup"
      subtitle="Coordinate with DTO (laptop + Google) and MIS (Microsoft email)"
    >
      <div className="space-y-3">
        <StepRow
          stepKey="dto_laptop_notified"
          label="Notify DTO — Laptop"
          hint="DTO procures and configures the work laptop."
          actor="DTO"
          workflow={workflow}
          onComplete={onComplete}
          onSkip={onSkip}
        />
        <StepRow
          stepKey="dto_google_workspace_notified"
          label="Notify DTO — Google Workspace Licence"
          hint="DTO procures the Google licence so HR can create the gmail account."
          actor="DTO"
          workflow={workflow}
          onComplete={onComplete}
          onSkip={onSkip}
        />
        <StepRow
          stepKey="mis_work_email_provisioned"
          label="MIS — Microsoft Work Email"
          hint="MIS creates the Microsoft mailbox; that address is the candidate's work email."
          actor="MIS"
          workflow={workflow}
          onComplete={onComplete}
          onSkip={onSkip}
        />
        <StepRow
          stepKey="hr_gmail_account_created"
          label="HR — Gmail Account Created"
          hint="HR creates the gmail account once DTO has provisioned the licence."
          actor="HR"
          workflow={workflow}
          onComplete={onComplete}
          onSkip={onSkip}
        />
        <StepRow
          stepKey="hr_gmail_group_added"
          label="HR — Added to Gmail Groups"
          hint="HR adds the new gmail to the relevant company groups."
          actor="HR"
          workflow={workflow}
          onComplete={onComplete}
          onSkip={onSkip}
        />
      </div>
    </SectionCard>
  </div>
);

const Day1Section = ({ application, workflow, onComplete, onSkip }) => {
  const isWP = application.track === TRACK_WORK_PERMIT;
  const isWPorSP = application.track === TRACK_WORK_PERMIT || application.track === TRACK_S_PASS_EP;

  return (
    <div className="space-y-4">
      <SectionCard
        title="Day 1 Readiness"
        subtitle="Last-mile checks so the candidate's first day runs smoothly"
      >
        <div className="space-y-3">
          <StepRow
            stepKey="orientation_scheduled"
            label="Orientation Scheduled"
            hint="HR books the orientation slot."
            actor="HR"
            workflow={workflow}
            onComplete={onComplete}
            onSkip={onSkip}
          />
          <StepRow
            stepKey="reporting_officer_briefed"
            label="Reporting Officer Briefed"
            hint="RO knows the start date, where to meet the new hire, and what to do."
            actor="HR or RO"
            workflow={workflow}
            onComplete={onComplete}
            onSkip={onSkip}
          />
          <StepRow
            stepKey="sic_scheduled"
            label="Safety Induction Scheduled"
            hint="Safety team books the SIC slot."
            actor="Safety"
            workflow={workflow}
            onComplete={onComplete}
            onSkip={onSkip}
          />
          {isWPorSP && (
            <StepRow
              stepKey="pcp_enrolled"
              label="PCP Enrolment"
              hint="Required for WP / S Pass holders only."
              actor="HR"
              workflow={workflow}
              onComplete={onComplete}
              onSkip={onSkip}
            />
          )}
          {isWP && (
            <StepRow
              stepKey="onboard_centre_scheduled"
              label="MOM Onboard Centre Slot"
              hint="Required for WP holders that are male, non-Malaysian, holding an IPA."
              actor="HR"
              workflow={workflow}
              onComplete={onComplete}
              onSkip={onSkip}
            />
          )}
        </div>
      </SectionCard>
    </div>
  );
};

const HireSection = ({ application, workflow, onHire }) => {
  const progress = computeProgress(workflow);
  const ready =
    application.status === STATUS_READY_TO_HIRE ||
    (workflow?.current_step_key === '__complete__' && application.status !== STATUS_HIRED);
  const alreadyHired = application.status === STATUS_HIRED;
  const blockedSteps = workflow?.steps?.filter((s) => s.status === 'blocked') || [];

  return (
    <div className="space-y-4">
      {alreadyHired && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <h3 className="text-xl font-bold text-green-900">Hired</h3>
          <p className="text-sm text-green-800 mt-1">
            {application.full_name} has been materialised into the staff database.
            {staffIdOf(application) ? ` Staff ID: ${staffIdOf(application)}.` : ''}
          </p>
        </div>
      )}

      {!alreadyHired && (
        <SectionCard title="Hire Readiness" subtitle="Final review before materialising into StaffUser">
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
              <div>
                <div className="text-sm font-medium text-gray-900">Workflow progress</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {progress.done} of {progress.total} steps complete ({progress.pct}%)
                </div>
              </div>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-600 h-2 rounded-full"
                  style={{ width: `${progress.pct}%` }}
                />
              </div>
            </div>

            {blockedSteps.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {blockedSteps.length} blocked step{blockedSteps.length > 1 ? 's' : ''}
                </div>
                <ul className="text-xs text-amber-900 space-y-1">
                  {blockedSteps.map((s) => (
                    <li key={s.key}>• {s.label || s.key}</li>
                  ))}
                </ul>
              </div>
            )}

            <button
              type="button"
              className="btn btn-admin-primary w-full justify-center"
              onClick={onHire}
              disabled={!ready}
              title={
                ready
                  ? 'Materialise into StaffUser + HRProfile + LeaveBalance'
                  : 'Complete all required steps and mark application as Ready to Hire first'
              }
            >
              <UserCheck className="w-5 h-5 mr-2" />
              {ready ? 'Hire Now' : 'Not Ready Yet'}
              {ready && <ArrowRight className="w-4 h-4 ml-2" />}
            </button>

            {!ready && (
              <p className="text-xs text-gray-500 text-center">
                Hiring becomes available once the workflow is complete and the application is
                marked <strong>Ready to Hire</strong> from the actions menu.
              </p>
            )}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT — OnboardingBoard
// ============================================================================

const OnboardingBoard = ({ initialPublicRef = null, onActiveCountChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [applications, setApplications] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [queueFilter, setQueueFilter] = useState(QUEUE_ACTIVE);
  const [triageFilter, setTriageFilter] = useState(TRIAGE_ANY);
  const [showNewModal, setShowNewModal] = useState(false);
  const adoptedUrlRef = useRef(false);
  const skipReconcileRef = useRef(false);

  // selectedRef priority: explicit URL prop > ?ref= query > nothing.
  const urlRef = searchParams.get('ref') || initialPublicRef;
  const [selectedRef, setSelectedRef] = useState(urlRef);
  const [activeSection, setActiveSection] = useState(searchParams.get('section') || 'profile');

  // Detail-pane state
  const [application, setApplication] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [uploadingDocType, setUploadingDocType] = useState(null);

  // Adopt the path-style deep-link the moment we mount, then keep URL in
  // sync as the user clicks around. We use replace: true so back-button
  // goes to the previous page instead of cycling through every selection.
  useEffect(() => {
    if (initialPublicRef && initialPublicRef !== searchParams.get('ref')) {
      const next = new URLSearchParams(searchParams);
      next.set('ref', initialPublicRef);
      setSearchParams(next, { replace: true });
      setSelectedRef(initialPublicRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPublicRef]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (selectedRef) next.set('ref', selectedRef);
    else next.delete('ref');
    if (activeSection) next.set('section', activeSection);
    else next.delete('section');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRef, activeSection]);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const resp = await preOnboardingAPI.listApplications();
      const data = resp.data;
      const rows = Array.isArray(data) ? data : data.results || data.workflows || [];
      setApplications(rows);
    } catch (err) {
      console.error('Failed to load candidate list', err);
      toast.error('Could not load candidates.');
      setApplications([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (ref) => {
    if (!ref) {
      setApplication(null);
      setWorkflow(null);
      setDocuments([]);
      return;
    }
    setDetailLoading(true);
    try {
      const [appResp, workflowResp, docsResp] = await Promise.all([
        preOnboardingAPI.getApplication(ref),
        preOnboardingAPI.getApplicationWorkflow(ref).catch(() => ({ data: null })),
        preOnboardingAPI.listDocuments(ref).catch(() => ({ data: { documents: [] } })),
      ]);
      setApplication(appResp.data);
      setWorkflow(workflowResp.data);
      const docsData = docsResp.data;
      setDocuments(
        Array.isArray(docsData) ? docsData : docsData?.documents || docsData?.results || []
      );
    } catch (err) {
      console.error('Failed to load candidate detail', err);
      if (err?.response?.status === 404) {
        toast.error('Candidate not found.');
        setSelectedRef(null);
      } else {
        toast.error('Could not load candidate detail.');
      }
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadDetail(selectedRef);
  }, [selectedRef, loadDetail]);

  // Deep-link / leftover ?ref= to a closed hire must open the Hired archive,
  // not keep that person selected on the Active work queue.
  useEffect(() => {
    if (listLoading || adoptedUrlRef.current || applications.length === 0) return;
    adoptedUrlRef.current = true;
    if (!urlRef) return;
    const app = applications.find((a) => a.public_ref === urlRef);
    if (app) {
      skipReconcileRef.current = true;
      setQueueFilter(queueForApplication(app));
      setSelectedRef(urlRef);
    }
  }, [listLoading, applications, urlRef]);

  const filteredApplications = useMemo(
    () =>
      filterOnboardingApplications(applications, {
        queueId: queueFilter,
        triageId: triageFilter,
        searchTerm,
      }),
    [applications, queueFilter, triageFilter, searchTerm],
  );

  const counts = useMemo(() => queueCounts(applications), [applications]);

  useEffect(() => {
    onActiveCountChange?.(counts[QUEUE_ACTIVE]);
  }, [counts, onActiveCountChange]);

  useEffect(() => {
    if (listLoading) return;
    if (skipReconcileRef.current) {
      skipReconcileRef.current = false;
      return;
    }
    setSelectedRef((current) => reconcileSelection(filteredApplications, current));
  }, [listLoading, filteredApplications]);

  // ----------------------------------------------------------------------
  // Action handlers
  // ----------------------------------------------------------------------

  const handlePatch = useCallback(
    async (patch) => {
      if (!selectedRef) return;
      if (isHiredStatus(application?.status)) {
        toast.info('This person is already hired. Edit the employee record instead.');
        return;
      }
      try {
        const resp = await preOnboardingAPI.updateApplication(selectedRef, patch);
        setApplication(resp.data);
        // Refresh the list so the row reflects (e.g.) a renamed candidate.
        loadList();
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          Object.values(err?.response?.data || {})[0]?.[0] ||
          'Save failed';
        toast.error(typeof detail === 'string' ? detail : 'Save failed');
        throw err;
      }
    },
    [selectedRef, loadList, application?.status]
  );

  const handleUploadDoc = useCallback(
    async (docType, file) => {
      if (!selectedRef) return;
      setUploadingDocType(docType);
      try {
        await preOnboardingAPI.uploadDocument(selectedRef, docType, file);
        toast.success(`Uploaded ${DOCUMENT_TYPE_LABELS[docType]}`);
        // Refresh docs + workflow (an upload may auto-complete a step).
        const [docsResp, workflowResp] = await Promise.all([
          preOnboardingAPI.listDocuments(selectedRef),
          preOnboardingAPI.getApplicationWorkflow(selectedRef).catch(() => ({ data: workflow })),
        ]);
        const docsData = docsResp.data;
        setDocuments(
          Array.isArray(docsData) ? docsData : docsData?.documents || docsData?.results || []
        );
        if (workflowResp.data) setWorkflow(workflowResp.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Upload failed.');
      } finally {
        setUploadingDocType(null);
      }
    },
    [selectedRef, workflow]
  );

  const handleSubmitWP = useCallback(
    async ({ passType, submittedAt }) => {
      if (!selectedRef) return;
      try {
        await preOnboardingAPI.captureWorkPassSubmission(selectedRef, {
          passType,
          submittedAt: submittedAt || null,
        });
        toast.success('WP submission recorded.');
        loadDetail(selectedRef);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not record submission.');
      }
    },
    [selectedRef, loadDetail]
  );

  const handleCaptureIPA = useCallback(
    async ({ passType, ipaIssuedAt, file }) => {
      if (!selectedRef) return;
      if (!file) {
        toast.error('Please attach the IPA letter file first.');
        return;
      }
      try {
        await preOnboardingAPI.captureIPA(selectedRef, {
          passType,
          ipaIssuedAt: ipaIssuedAt || null,
          file,
        });
        toast.success('IPA captured.');
        loadDetail(selectedRef);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not capture IPA.');
      }
    },
    [selectedRef, loadDetail]
  );

  const handleCompleteStep = useCallback(
    async (stepKey) => {
      if (!selectedRef) return;
      try {
        await preOnboardingAPI.completeStep(selectedRef, stepKey);
        toast.success('Step marked done.');
        loadDetail(selectedRef);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not complete step.');
      }
    },
    [selectedRef, loadDetail]
  );

  const handleSkipStep = useCallback(
    async (stepKey, reason) => {
      if (!selectedRef) return;
      try {
        await preOnboardingAPI.skipStep(selectedRef, stepKey, reason);
        toast.success('Step skipped.');
        loadDetail(selectedRef);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not skip step.');
      }
    },
    [selectedRef, loadDetail]
  );

  const handleHire = useCallback(async () => {
    if (!selectedRef) return;
    if (!window.confirm('Materialise this candidate into the staff database now?')) return;
    try {
      // Hire is exposed on the application detail endpoint as a POST sub-action.
      // We hit the URL directly because the JS SDK doesn't have a typed wrapper
      // for it yet.
      await preOnboardingAPI.transitionApplication(selectedRef, STATUS_HIRED);
      toast.success('Hired. Staff record is being created.');
      // Stay on this person so HR can read the new staff ID. They leave
      // Active, so the list must follow them into the hire archive.
      setQueueFilter(QUEUE_HIRED);
      loadDetail(selectedRef);
      loadList();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          'Hire failed. Ensure all required steps are complete and the status is Ready to Hire.'
      );
    }
  }, [selectedRef, loadDetail, loadList]);

  const handleCopyLink = useCallback(() => {
    if (!application) return;
    // Magic-link URL pattern — see staff_confidential.pre_onboarding_emails
    // and frontend/src/pages/CandidatePortal.js. We surface a templated URL
    // here; the candidate token is minted server-side when the welcome email
    // fires, so HR copy/pastes the public_ref-scoped URL as a quick fallback.
    const url = `${window.location.origin}/candidate-portal/${application.public_ref}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success('Candidate portal link copied.'),
      () => toast.error('Could not copy link.')
    );
  }, [application]);

  const handleOpenPortalPreview = useCallback(() => {
    if (!application) return;
    window.open(`/candidate-portal/${application.public_ref}`, '_blank', 'noopener');
  }, [application]);

  const handleNewCreated = useCallback(
    (created) => {
      setShowNewModal(false);
      setQueueFilter(QUEUE_ACTIVE);
      setTriageFilter(TRIAGE_ANY);
      setSelectedRef(created.public_ref);
      setActiveSection('profile');
      loadList();
    },
    [loadList]
  );

  // ----------------------------------------------------------------------
  // Derived data
  // ----------------------------------------------------------------------

  const pane = QUEUE_PANE[queueFilter] || QUEUE_PANE[QUEUE_ACTIVE];

  const sectionStates = useMemo(() => computeSectionStates(workflow), [workflow]);

  const visibleSections = useMemo(() => {
    if (!application) return SECTIONS;
    const isLocalPR = application.track === TRACK_LOCAL_PR;
    return SECTIONS.filter((s) => !s.requiresWP || !isLocalPR);
  }, [application]);

  // ----------------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------------

  return (
    <div className="flex gap-4 h-[calc(100vh-220px)] min-h-[600px]">
      {/* LEFT PANE — candidate list */}
      <aside className="w-[340px] flex-shrink-0 bg-white rounded-2xl shadow-sm border flex flex-col overflow-hidden">
        <div className="p-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h2 className="text-base font-semibold text-gray-900 truncate min-w-0">
              {pane.title}
            </h2>
            <button
              type="button"
              onClick={() => setShowNewModal(true)}
              className="btn btn-success btn-sm flex-shrink-0"
            >
              <Plus className="w-4 h-4 mr-1" />
              New
            </button>
          </div>
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder={pane.searchPlaceholder}
          />
          <div className="mt-3">
            <PillToggle
              theme="admin"
              variant="text"
              fullWidth
              ariaLabel="Onboarding queue"
              value={queueFilter}
              onChange={(id) => {
                setQueueFilter(id);
                setTriageFilter(TRIAGE_ANY);
              }}
              options={QUEUE_OPTIONS.map((opt) => ({
                ...opt,
                count: counts[opt.id],
              }))}
            />
          </div>
          {queueFilter === QUEUE_ACTIVE && (
            <div className="mt-3">
              <PillSelect
                theme="admin"
                icon={Inbox}
                iconColor="text-red-600"
                minWidth="min-w-0"
                className="w-full"
                value={triageFilter}
                onChange={setTriageFilter}
                options={TRIAGE_OPTIONS}
              />
            </div>
          )}
          {pane.hint && (
            <p className="mt-3 text-xs text-gray-600 leading-relaxed">{pane.hint}</p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto divide-y">
          {listLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-12 px-4">
              {searchTerm ? (
                <>
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No matching candidates</h3>
                  <p className="text-sm text-gray-500">Try a different name, ref, or staff ID.</p>
                </>
              ) : applications.length === 0 ? (
                <>
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Inbox className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No candidates yet</h3>
                  <p className="text-sm text-gray-500 max-w-sm mx-auto">
                    Start a hire from New. ATS-accepted offers also land here.
                  </p>
                </>
              ) : queueFilter === QUEUE_ACTIVE ? (
                <>
                  <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Queue is clear</h3>
                  <p className="text-sm text-gray-500">
                    No one is waiting to be hired. Completed packets stay under Hired
                    if you need the offer, work pass, or staff ID later.
                  </p>
                </>
              ) : queueFilter === QUEUE_HIRED ? (
                <>
                  <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No hire packets yet</h3>
                  <p className="text-sm text-gray-500">
                    People appear here after you hire them. Day-1 work is on the
                    employee record.
                  </p>
                </>
              ) : (
                <>
                  <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No candidates in this view</h3>
                  <p className="text-sm text-gray-500">
                    Switch to Active or Hired to see other records.
                  </p>
                </>
              )}
            </div>
          ) : (
            filteredApplications.map((app) => (
              <CandidateListItem
                key={app.public_ref}
                application={app}
                isSelected={app.public_ref === selectedRef}
                onSelect={() => setSelectedRef(app.public_ref)}
              />
            ))
          )}
        </div>
      </aside>

      {/* RIGHT PANE — detail */}
      <main className="flex-1 bg-white rounded-2xl shadow-sm border overflow-hidden flex flex-col">
        {!selectedRef ? (
          <div className="flex-1 flex items-center justify-center text-center p-12">
            <div>
              {queueFilter === QUEUE_ACTIVE && counts[QUEUE_HIRED] > 0 && counts[QUEUE_ACTIVE] === 0 ? (
                <>
                  <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Onboarding queue is clear</h3>
                  <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                    No one is waiting to be hired. Use Hired only when you need a
                    completed hire packet — not as the next work step.
                  </p>
                </>
              ) : (
                <>
                  <UserCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No candidate selected</h3>
                  <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                    Select a candidate from the list, or click New to add one.
                  </p>
                </>
              )}
            </div>
          </div>
        ) : detailLoading || !application ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadingSpinner message="Loading candidate…" />
          </div>
        ) : (
          <>
            {/* Header + tabs are flex-shrink-0 (fixed-height) so the content
                area below scrolls independently. This avoids `position: sticky`
                fighting between two stacked sticky elements. */}
            <CandidateHeader
              application={application}
              workflow={workflow}
              onCopyLink={handleCopyLink}
              onOpenPortalPreview={handleOpenPortalPreview}
            />
            <SectionTabs
              sections={visibleSections}
              active={activeSection}
              onChange={setActiveSection}
              sectionStates={sectionStates}
            />
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {isHiredStatus(application.status) && (
                <HiredArchiveBanner application={application} />
              )}
              {isAwaitingHrReview(application) && activeSection !== 'profile' && (
                <PendingReviewBanner onGoToProfile={() => setActiveSection('profile')} />
              )}
              {activeSection === 'profile' && (
                <ProfileSection application={application} onPatch={handlePatch} />
              )}
              {activeSection === 'offer' && (
                <OfferLOASection
                  application={application}
                  documents={documents}
                  onPatch={handlePatch}
                  onUpload={handleUploadDoc}
                  busy={uploadingDocType}
                />
              )}
              {activeSection === 'identity' && (
                <IdentitySection
                  documents={documents}
                  onUpload={handleUploadDoc}
                  busy={uploadingDocType}
                />
              )}
              {activeSection === 'workpass' && (
                <WorkPassSection
                  application={application}
                  documents={documents}
                  workflow={workflow}
                  onSubmitWP={handleSubmitWP}
                  onCaptureIPA={handleCaptureIPA}
                  onUpload={handleUploadDoc}
                  busy={uploadingDocType}
                />
              )}
              {activeSection === 'banking' && (
                <BankingSection application={application} onPatch={handlePatch} />
              )}
              {activeSection === 'it' && (
                <ITSection
                  workflow={workflow}
                  onComplete={handleCompleteStep}
                  onSkip={handleSkipStep}
                />
              )}
              {activeSection === 'day1' && (
                <Day1Section
                  application={application}
                  workflow={workflow}
                  onComplete={handleCompleteStep}
                  onSkip={handleSkipStep}
                />
              )}
              {activeSection === 'hire' && (
                <HireSection
                  application={application}
                  workflow={workflow}
                  onHire={handleHire}
                />
              )}
            </div>
          </>
        )}
      </main>

      <NewOnboardingModal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreated={handleNewCreated}
      />
    </div>
  );
};

export default OnboardingBoard;
